import {ApiResponseDto, Claim, VivoRootModel} from "@utils/Models";
import {getOneLoginUser} from "@utils/onelogin/OneLoginHelper";
import axios from "axios";
import {AuthenticationResponse, LoginModel} from "./auth.models";
import {
    ApiUrl_Login_ExchangeOneLoginToken,
    ApiUrl_Login_ImpersonateMember,
    ApiUrl_Login_Login,
    ApiUrl_Login_VerifyMember
} from "@utils/Endpoints";
import {JwtUtils} from "./JwtUtils";
import {boolean} from "yup";
import {
    beginContextRequest,
    invalidateContextRequests,
    isSuperseded,
    runTokenIssuingRequest,
    Superseded,
    SUPERSEDED
} from "./contextRequestGuard";
import {withCustomerManagementDenied} from "./customerManagementAccess";

/**
 * Adopts the token of an impersonation response and returns the root model to put into the context until the app
 * is re-initialized (the caller reloads the page, startup then runs VerifyUser with the new token).
 * The root model of the response was built under the caller's (SuperAdmin) request context, so the Customer
 * Management capability in it is explicitly denied; context requests of the previous identity are invalidated.
 */
export function adoptImpersonationResponse(authResponse: AuthenticationResponse): VivoRootModel {
    JwtUtils.impersonateUser(authResponse);
    invalidateContextRequests();
    return withCustomerManagementDenied(authResponse.RootModel);
}

/**
 * Impersonation transition for the auth context: adopts the impersonation token FIRST and only then reads the claims,
 * so the context receives the target user's claims (not the previous administrator's) together with the
 * denied-until-verified root model. The caller reloads the page afterwards (startup runs VerifyUser).
 */
export function applyImpersonationToContext(
    authResponse: AuthenticationResponse,
    update: (claims: Claim[], rootModel?: VivoRootModel) => void
) {
    const rootModel = adoptImpersonationResponse(authResponse);
    update(JwtUtils.getClaims(), rootModel);
}

/** Result of a successful credentials login: the issued response and the authoritative (VerifyUser) root model. */
export interface CredentialsLoginResult {
    authResponse: AuthenticationResponse;
    rootModel: VivoRootModel;
}

export default function useAuthenticationHelper() {
    /** checks if there is onelogin token. if not, returns null. Checks if the token is valid and obtains Vivo token along with RootModel.
     * saves the Vivo token and returns the root model or error message.
     * Returns SUPERSEDED when a newer context request or token replaced this one at any point before or while it ran,
     * including the pending OneLogin lookup / token exchange (the caller must do nothing; nothing was saved). */
    async function validateOneLoginUserAsync(): Promise<VivoRootModel | string | Superseded> {
        const issued = await runTokenIssuingRequest(async (isCurrent:()=>boolean) => {
            const user = await getOneLoginUser();
            if (!user || !isCurrent())
                return null;
            //return "OneLogin token was invalid!";
            try {
                let response = await axios.post<ApiResponseDto<AuthenticationResponse>>(ApiUrl_Login_ExchangeOneLoginToken, user);
                return {authResponse: response.data.Result};
            } catch (error) {
                //@ts-ignore
                const errorMessage = error?.errorMessages?.[0] ?? error.message ?? error;
                //window.location.replace(`/en/access-denied`);
                return {error: errorMessage as string};
            }
        });
        if (isSuperseded(issued))
            return SUPERSEDED;
        if (!issued)
            return "";
        if (issued.error !== undefined)
            return issued.error;
        try {
            // the root model of a token-issuing response is not authoritative: verify with the new token first
            return await adoptTokenIssuingResponseAsync(issued.authResponse!);
        } catch (error: any) {
            return error?.errorMessages?.[0] ?? error?.message ?? String(error);
        }
    }

    /** tries to use saved token to authorize connection to vivo portal backend (VerifyUser). if it works (token is valid), returns fresh root model.
     * if it doesn't work, returns error message.
     * This is the single place that owns the freshness check of a verification: the result is SUPERSEDED (success and
     * failure alike) when a newer context request started or the token changed while it was in flight. */
    async function validateUmbracoTokenAsync(): Promise<VivoRootModel | string | Superseded> {
        const isCurrent = beginContextRequest();
        try {
            let response = await axios.post<ApiResponseDto<VivoRootModel>>(ApiUrl_Login_VerifyMember);
            return isCurrent() ? response.data.Result : SUPERSEDED;
        } catch (error : any) {
            if (!isCurrent())
                return SUPERSEDED;
            //@ts-ignore
            if (error?.errorMessages?.length > 0) {
                //@ts-ignore
                return error?.errorMessages?.[0];
            }
            return error.message ?? "unknown error";

        }
    }

    /**
     * Adopts a token-issuing AuthenticationResponse (OneLogin exchange, local login): saves the new token, invalidates
     * context requests of the previous token and fetches the authoritative root model with VerifyUser using the new
     * token. The root model inside the response is never trusted for Customer Management: when VerifyUser fails the
     * returned model is that root model with the capability explicitly denied. Returns SUPERSEDED when a newer context
     * request or token superseded the verification (the caller must then not touch the context). The freshness
     * generation of the verification is owned by the VerifyUser call only; the token-issuing request that produced
     * `authResponse` must already have passed its own freshness check (`runTokenIssuingRequest`) - invalidating that
     * earlier generation here is the deliberate hand-over, not a self-invalidation.
     */
    async function adoptTokenIssuingResponseAsync(authResponse: AuthenticationResponse): Promise<VivoRootModel | Superseded> {
        JwtUtils.saveToken(authResponse);
        invalidateContextRequests();
        const verified = await validateUmbracoTokenAsync();
        if (isSuperseded(verified))
            return SUPERSEDED;
        if (typeof verified === "string" || !verified)
            return withCustomerManagementDenied(authResponse.RootModel);
        return verified;
    }

    /**
     * Local credentials login. The pending LoginMember response is guarded: when a newer identity/context request
     * superseded it, its token is never saved and the outcome is SUPERSEDED (an obsolete failure is not reported).
     * Returns the issued response with the VerifyUser root model, or an error message to show (empty = nothing to show).
     */
    async function loginWithCredentialsAsync(model: LoginModel): Promise<CredentialsLoginResult | string | Superseded> {
        try {
            const response = await runTokenIssuingRequest(() =>
                axios.post<ApiResponseDto<AuthenticationResponse>>(ApiUrl_Login_Login, model));
            if (isSuperseded(response))
                return SUPERSEDED;
            if (response.status !== 200)
                return "";
            const data = response.data;
            if (data.ErrorMessages.length)
                return data.ErrorMessages[0];
            const rootModel = await adoptTokenIssuingResponseAsync(data.Result);
            if (isSuperseded(rootModel))
                return SUPERSEDED;
            return {authResponse: data.Result, rootModel};
        } catch (error: any) {
            return error?.toString() + "";
        }
    }

    /**
     * Requests an impersonation token for a member. The pending response is guarded like any token-issuing request:
     * a superseded response is returned as SUPERSEDED and its token is never adopted. Returns the issued response
     * (to be applied with `applyImpersonationToContext`), or an error message to show.
     */
    async function requestImpersonationAsync(memberId: number): Promise<AuthenticationResponse | string | Superseded> {
        try {
            const response = await runTokenIssuingRequest(() =>
                axios.post<ApiResponseDto<AuthenticationResponse>>(ApiUrl_Login_ImpersonateMember, memberId));
            if (isSuperseded(response))
                return SUPERSEDED;
            if (response.status !== 200)
                return "";
            if (response.data.ErrorMessages.length)
                return response.data.ErrorMessages[0];
            return response.data.Result;
        } catch (error: any) {
            return error?.toString() + "";
        }
    }

    return {
        validation: {
            validateOneLoginUserAsync,
            validateUmbracoTokenAsync,
            adoptTokenIssuingResponseAsync,
            loginWithCredentialsAsync,
            requestImpersonationAsync
        }
    }
}
